/**
 * BrokerageStatusBadge — renders the effective brokerage contract status as a
 * coloured badge.
 *
 * Status values come from the shared enum in @dallali/shared:
 *   pending | active | expiring | expired | renew
 * The status → label/variant mapping (tolerant of unknown values) lives in
 * ./brokerageStatus so plain-text surfaces can reuse the same labels.
 *
 * Salvaged from origin/feat/brokerage-contracts (PR #1344) and updated to:
 *   - Remove "cancelled"/"none" which do not exist in the Nest model.
 *   - Add "expiring" and "renew" (the two new statuses).
 *   - Remove direct type reference to the old @/types/brokerage "union" type.
 */
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { brokerageStatusConfig, brokerageStatusLabel } from "./brokerageStatus";

interface BrokerageStatusBadgeProps {
  status: string;
  className?: string;
}

export function BrokerageStatusBadge({
  status,
  className,
}: BrokerageStatusBadgeProps) {
  const { t } = useLanguage();
  const config = brokerageStatusConfig(status);

  return (
    <Badge
      variant={config.variant}
      className={className}
      data-testid="brokerage-status-badge"
      data-status={status}
    >
      {brokerageStatusLabel(status, t)}
    </Badge>
  );
}
